import { Injectable } from '@angular/core';
import { SignalR, ISignalRConnection, SignalRConfiguration, BroadcastEventListener } from '@henkkelder/ng2-signalr';
import { IRealtimeUpdatesProvider } from '@common/interfaces/i-realtime-updates-provider';
import { Record } from '@common/data-models/records/record';
import { AbstractServiceRecord } from '@common/data-models/service/abstract-service-record';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { Convertor } from '@common/services/convertor';
import { ObjectTypes } from '@common/enums/object-types';

import { LogonService } from "@common/services/logon-service";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { Subscription } from 'rxjs';

declare var serverConfig: { [id: string]: any };

@Injectable({ providedIn: 'root' })
export class RealtimeUpdatesSignalR implements IRealtimeUpdatesProvider {
    private convertTimeStart: number;
    private convertTime: number = 0;
    private rtuTimeStart: number;
    private rtuTime: number = 0;
    private signalRConnection: ISignalRConnection;
    private addUpdateListener: BroadcastEventListener<any>;
    private moveListener: BroadcastEventListener<any>;
    private handler: (rtu: Record, providerName?: string) => void;
    private disconnectHandler: () => void = null;
    private disconnecting: boolean = false;
    private signalRConnectionStatusSub: Subscription;
    private addUpdateListenerSub: Subscription;
    private moveListenerSub: Subscription;
    private isBuddy: boolean = false;

    constructor(
        private signalR: SignalR,
        public Convertor: Convertor,
        public nomWebServicesJson: NomWebServicesJson
    ) {
    }

    public getRTUTime(): number {
        return this.rtuTime;
    }

    public getConvertTime(): number {
        return this.convertTime;
    }

    public getIsBuddy(): boolean {
        return this.isBuddy;
    }

    public async connect(endpointUrl: string, handler: (rtu: Record, providerName?: string) => void, buddy: boolean = false) {
        this.isBuddy = buddy;
        this.handler = handler;
        const c = new SignalRConfiguration();
        c.hubName = 'RealTimeUpdatesHub';
        c.url = endpointUrl + '/signalR';
        c.logging = true;
        if (this.signalRConnection) {
            try {
                this.forceDisconnect();
                this.signalRConnection = await this.signalR.connect(c);
            } catch (e) {
                console.log(e);
            }
        } else {
            this.signalRConnection = await this.signalR.connect(c);
        }
        this.signalRConnectionStatusSub = this.signalRConnection.status.subscribe(async (connectionStatus) => {
            if (connectionStatus.value == 2) {
                this.signalRConnection.stop();
            }
            if (connectionStatus.value == 4 && !this.disconnecting) {
                this.forceDisconnect();
                if (this.disconnectHandler) this.disconnectHandler();

            }
        });
        this.addUpdateListener = this.signalRConnection.listenFor("AddUpdateRecord");
        this.addUpdateListenerSub = this.addUpdateListener.subscribe((rec: AbstractServiceRecord) => {
            this.internalHandler(rec);
        });
        this.moveListener = this.signalRConnection.listenFor("MoveRecordPST");
        this.moveListenerSub = this.moveListener.subscribe((rec: AbstractServiceRecord) => {
            this.internalHandler(rec);
        });
    }

    public forceDisconnect() {
        try {
            this.disconnecting = true;
            this.signalRConnectionStatusSub.unsubscribe();
            this.signalRConnectionStatusSub = null;
            this.addUpdateListenerSub.unsubscribe();
            this.addUpdateListenerSub = null;
            this.moveListenerSub.unsubscribe();
            this.signalRConnection.stop();
            this.moveListenerSub = null;
            this.disconnecting = false;
            this.signalRConnection = null;
        } catch (e) { }
    }

    public registerDisconnectHandler(disconnectHandler: () => void) {
        this.disconnectHandler = disconnectHandler;
    }

    public getSubscriptionId(rec: Record): string {
        return this.getSubscriptionIdForPath(rec.database, rec.path.fullPath);
    }

    public getSubscriptionIdForPath(database: string, path: string): string {
        return "D=" + database + "&P=" + path;
    }

    public async subscribe(subscriptionId: string) {
        try {
            while (!this.signalRConnection) {
                await (new Promise(resolve => setTimeout(resolve, 1000)));
            }
            await this.signalRConnection.invoke("JoinGroup", subscriptionId);
        } catch (e) {
            console.log(e);
        }
    }

    private internalHandler(rec: AbstractServiceRecord) {
        try {
            if (rec.IsCollectionDiff) {
                this.convertTimeStart = Date.now();
                let serviceCollectionRecord = ServiceCollectionRecord.instance(rec);
                let collectionRecord = this.Convertor.fromServiceCollectionRecord(serviceCollectionRecord);
                if (!collectionRecord) {
                    // Couldn't build collection record - ignore the RTU
                    return;
                }
                this.convertTime = Date.now() - this.convertTimeStart;
                this.rtuTimeStart = Date.now();
                collectionRecord.isCollectionDiff = true;
                this.handler(collectionRecord, "enps");
                this.rtuTime = Date.now() - this.rtuTimeStart;
            } else {
                if (ObjectTypes.isCollectionType(rec.ListData.Type)) {
                    let serviceCollectionRecord = ServiceCollectionRecord.instance(rec);
                    let collectionRecord = this.Convertor.fromServiceCollectionRecord(serviceCollectionRecord);
                    this.rtuTimeStart = Date.now();
                    collectionRecord.isCollectionDiff = false;
                    this.handler(collectionRecord, "enps");
                    this.rtuTime = Date.now() - this.rtuTimeStart;
                } else {
                    let serviceRecord: ServiceRecord = Object.assign(new ServiceRecord, rec);
                    serviceRecord.instantiateChildren();
                    let record = this.Convertor.fromServiceRecord(serviceRecord);
                    this.handler(record, "enps");
                }
            }
        } catch (e) {
            console.log(e)
        }
    }
}
